import {useDisclosure} from '@chakra-ui/react';
import {createCustomerAddress, getCustomerAddresses, updateCustomerAddress} from 'api/requests';
import {AddAddressModal} from 'components/shared';
import {useAuth, useLocalStorageState, useToast, useTranslation} from 'hooks';
import {useEffect, useState} from 'react';
import {ICustomerAddress, StorageKeyEnum} from 'types';
import logger from 'utils/logger';
import {v4 as uuidv4} from 'uuid';

import {AddressContext, type ITypeAddressCourierExpress} from './contexts/AddressContext';
import {useCustomerContext} from './contexts/CustomerContext';

const AddressProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  const {isAuthenticated} = useAuth();

  const toast = useToast();
  const {t} = useTranslation();
  const {refetchCustomer, isLoadingGetting} = useCustomerContext();

  // courier-express
  const [typeAddressCourierExpress, setTypeAddressCourierExpress] = useState<ITypeAddressCourierExpress>();
  const [selectedStartAddress, setSelectedStartAddress] = useState<ICustomerAddress>();
  const [selectedEndAddress, setSelectedEndAddress] = useState<ICustomerAddress>();

  const [savedAddress, setSavedAddress, {removeItem: removeSavedAddress}] = useLocalStorageState<ICustomerAddress>(
    StorageKeyEnum.SAVED_ADDRESS,
  );
  const [coordinatesAddress, setCoordinates, {removeItem: removeCoordinates}] = useLocalStorageState(
    StorageKeyEnum.COORDINATES,
  );
  const [selectedAddress, setSelectedAddress] = useState<ICustomerAddress>();
  const [addressIdToEdit, setAddressIdToEdit] = useState<number | string>();
  const [addresses, setAddresses] = useState<ICustomerAddress[] | undefined>(savedAddress ? [savedAddress] : undefined);
  const [mapAddressChanged, setMapAddressChanged] = useState<boolean>(false);

  const {
    onOpen: onOpenAddAddressModal,
    isOpen: isOpenAddAddressModal,
    onClose: onCloseAddAddressModal,
  } = useDisclosure();

  const [isAddressesLoading, setIsAddressesLoading] = useState(true);

  const getAddresses = async () => {
    if (!isAuthenticated) return setIsAddressesLoading(false);
    setIsAddressesLoading(true);
    const data = await getCustomerAddresses();
    setAddresses(data);
    setIsAddressesLoading(false);
  };

  useEffect(() => {
    const latitude = selectedAddress?.address.latitude;
    const longitude = selectedAddress?.address.longitude;

    if (latitude && longitude) {
      setCoordinates(`${latitude},${longitude}`);
      logger.setContext('ProviderAddress', selectedAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  useEffect(() => {
    logger.logDebug(`Effect: ${addresses}`, 'AddressProvider');
    if (addresses?.length) {
      const mainAddress = addresses.find((address) => address.is_main);
      setSelectedAddress(mainAddress);
    }
  }, [addresses]);

  useEffect(() => {
    if (savedAddress) setAddresses([savedAddress]);
  }, [savedAddress]);

  useEffect(() => {
    const saveAdress = async () => {
      if (!savedAddress) return;
      delete savedAddress?.id;
      try {
        await createNewAddress(savedAddress);
        removeSavedAddress();
      } catch (error) {
        logger.addBreadcrumb('save saved address', savedAddress);
        logger.error(new Error('create address err'), error);
      }
    };
    if (!isAuthenticated) return;
    saveAdress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoadingGetting) return setIsAddressesLoading(true);
    if (!isAuthenticated) return setIsAddressesLoading(false);
    getAddresses();
    logger.logDebug(`Effect: get customer-addresses`, 'AddressProvider');
    // otherwise infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoadingGetting]);

  const onSetTypeAddressCourierExpressHelper = (type: ITypeAddressCourierExpress) => {
    setTypeAddressCourierExpress(type);
  };

  const openAddAddressModal = (addressId?: number | string) => {
    if (addressId) setAddressIdToEdit(addressId);

    onOpenAddAddressModal();
  };

  const closeAddAddressModal = () => {
    if (addressIdToEdit) setAddressIdToEdit(undefined);
    onCloseAddAddressModal();
  };

  const createNewAddress = async (address: ICustomerAddress) => {
    //Set start and end address for courier-express
    if (typeAddressCourierExpress === 'start') {
      setSelectedStartAddress(address);
    } else if (typeAddressCourierExpress === 'end') {
      setSelectedEndAddress(address);
    } else {
      let savedAddress: ICustomerAddress;
      try {
        if (!isAuthenticated) {
          savedAddress = {
            ...address,
            id: uuidv4(),
          };
          setSavedAddress(savedAddress);
        } else {
          await createCustomerAddress(address);
          getAddresses(); // address is refetched when customer is refetched | just when address is created
        }
      } catch (error) {
        toast.error(t('ADDRESS_CREATE_ERROR')); //Error on address create
      }
    }
  };

  const updateAddress = async (address: ICustomerAddress) => {
    let savedAddress: ICustomerAddress;
    try {
      if (!isAuthenticated) {
        savedAddress = {
          ...address,
          id: uuidv4(),
        };
        setSavedAddress(savedAddress);
        return;
      } else {
        if (addressIdToEdit) {
          savedAddress = await updateCustomerAddress(addressIdToEdit, address);
          refetchCustomer();
        }
      }
      toast.success('Success');

      setAddresses(
        addresses?.map((address) => {
          address.is_main = false;
          if (address.id === savedAddress.id) return savedAddress;
          return address;
        }),
      );
    } catch (error: any) {
      const strErr = error?.response?.data ? JSON.stringify(error?.response?.data) : '';
      toast.error(`${t('ADDRESS_UPDATE_ERROR')} ${strErr}`);
    }
  };

  const courierExpressAddress = typeAddressCourierExpress == 'start' ? selectedStartAddress : selectedEndAddress;
  return (
    <AddressContext.Provider
      value={{
        addresses,
        setSelectedAddress,
        coordinatesAddress,
        selectedAddress,
        selectedStartAddress,
        selectedEndAddress,
        onSetTypeAddressCourierExpressHelper,
        createNewAddress,
        updateAddress,
        openAddAddressModal,
        isAddressesLoading,
        mapAddressChanged,
        setMapAddressChanged,
      }}
    >
      {children}
      {isOpenAddAddressModal && (
        <AddAddressModal
          removeCoordinates={removeCoordinates}
          customerAddress={
            courierExpressAddress ? courierExpressAddress : addresses?.find((address) => address.id === addressIdToEdit)
          }
          createNewAddress={createNewAddress}
          updateAddress={updateAddress}
          setAddresses={setAddresses}
          isOpen={isOpenAddAddressModal}
          onClose={closeAddAddressModal}
          forcePrivateBuilding={!!typeAddressCourierExpress}
          isCourierExpress={typeAddressCourierExpress != undefined}
        />
      )}
    </AddressContext.Provider>
  );
};

export {AddressProvider};
